import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { Card, Metric, Text, Title } from '@tremor/react';
import { TrendingUp, Award, MapPin, Utensils } from 'lucide-react';

const apiUrlSeleccionMensual = process.env.REACT_APP_API_PEDIDOSMENSUALES;

const RankingSedes = ({ mes }) => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerDatos = async () => {
      try {
        setCargando(true);
        setError(null);
        let url = apiUrlSeleccionMensual;
        if (mes) {
          url += `?mes=${mes}`;
        }
        const res = await axios.get(url);
        console.log('Datos para ranking:', res.data);
        setDatos(res.data || []);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError(err.message);
      } finally {
        setCargando(false);
      }
    };
    obtenerDatos();
  }, [mes]);

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <Text>Cargando ranking...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <Text className="text-red-600">Error: {error}</Text>
      </div>
    );
  }

  if (!datos || datos.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
        <Text className="text-yellow-800">No hay datos disponibles para el ranking</Text>
      </div>
    );
  }

  // Análisis de datos
  const sedesStats = {};
  const tiposDeMenu = new Set();
  let totalPedidos = 0;

  datos.forEach((pedido) => {
    const sedeNombre = pedido.sede_nombre || 'Sin sede';
    const tipo = pedido.tipo || 'Sin tipo';
    
    tiposDeMenu.add(tipo);
    
    if (!sedesStats[sedeNombre]) {
      sedesStats[sedeNombre] = {
        total: 0,
        porTipo: {},
        direccion: pedido.sede_direccion || 'Sin dirección'
      };
    }
    
    sedesStats[sedeNombre].total++;
    sedesStats[sedeNombre].porTipo[tipo] = (sedesStats[sedeNombre].porTipo[tipo] || 0) + 1;
    totalPedidos++;
  });

  // Convertir a array y ordenar por total de pedidos
  const rankingSedes = Object.entries(sedesStats)
    .map(([nombre, stats]) => ({
      nombre,
      ...stats,
      porcentaje: ((stats.total / totalPedidos) * 100).toFixed(1)
    }))
    .sort((a, b) => b.total - a.total);

  // Sede más activa
  const sedeMasActiva = rankingSedes[0];

  // Tipo de menú más popular globalmente
  const tiposPorTotal = {};
  datos.forEach((p) => {
    const tipo = p.tipo || 'Sin tipo';
    tiposPorTotal[tipo] = (tiposPorTotal[tipo] || 0) + 1;
  });
  const tipoMasPopular = Object.entries(tiposPorTotal)
    .sort((a, b) => b[1] - a[1])[0];

  // Datos para gráfico de barras (sedes vs tipos de menú)
  const tiposArray = Array.from(tiposDeMenu);
  const sedesNombres = rankingSedes.map(s => s.nombre);
  
  const seriesBarSedes = tiposArray.map(tipo => ({
    name: tipo,
    data: rankingSedes.map(sede => sede.porTipo[tipo] || 0)
  }));

  // Datos para gráfico circular (distribución por sede)
  const seriesPieSedes = rankingSedes.map(s => s.total);
  const labelsPieSedes = rankingSedes.map(s => s.nombre);

  // Opciones para gráfico de barras
  const optionsBarSedes = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      foreColor: '#64748b',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 8,
        dataLabels: {
          position: 'center',
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: sedesNombres,
      labels: {
        style: {
          fontSize: '11px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      title: {
        text: 'Cantidad de Pedidos',
        style: {
          fontSize: '12px',
          fontWeight: 700,
        },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 600,
    },
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'],
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 4,
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (val) => `${val} pedidos`,
      },
    },
  };

  // Opciones para gráfico circular
  const optionsPieSedes = {
    chart: {
      type: 'donut',
      foreColor: '#64748b',
    },
    labels: labelsPieSedes,
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
    legend: {
      position: 'bottom',
      fontSize: '12px',
      fontWeight: 600,
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: {
        fontSize: '14px',
        fontWeight: 700,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Pedidos',
              fontSize: '14px',
              fontWeight: 700,
              color: '#1e293b',
              formatter: () => totalPedidos,
            },
          },
        },
      },
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (val) => `${val} pedidos (${((val / totalPedidos) * 100).toFixed(1)}%)`,
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card decoration="top" decorationColor="blue">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Award className="text-blue-600 w-6 h-6" />
            </div>
            <div>
              <Text>Sede Más Activa</Text>
              <Metric>{sedeMasActiva?.nombre || 'N/A'}</Metric>
              <Text className="text-xs text-slate-500 mt-1">
                {sedeMasActiva?.total} pedidos ({sedeMasActiva?.porcentaje}%)
              </Text>
            </div>
          </div>
        </Card>

        <Card decoration="top" decorationColor="green">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-3 rounded-xl">
              <Utensils className="text-green-600 w-6 h-6" />
            </div>
            <div>
              <Text>Menú Más Pedido</Text>
              <Metric className="text-lg">{tipoMasPopular?.[0] || 'N/A'}</Metric>
              <Text className="text-xs text-slate-500 mt-1">
                {tipoMasPopular?.[1]} pedidos
              </Text>
            </div>
          </div>
        </Card>

        <Card decoration="top" decorationColor="purple">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-3 rounded-xl">
              <TrendingUp className="text-purple-600 w-6 h-6" />
            </div>
            <div>
              <Text>Total de Pedidos</Text>
              <Metric>{totalPedidos}</Metric>
              <Text className="text-xs text-slate-500 mt-1">
                {rankingSedes.length} sedes activas
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de barras apiladas */}
        <Card>
          <Title className="mb-4">Pedidos por Sede y Tipo de Menú</Title>
          <Chart
            options={optionsBarSedes}
            series={seriesBarSedes}
            type="bar"
            height={350}
          />
        </Card>

        {/* Gráfico circular */}
        <Card>
          <Title className="mb-4">Distribución de Pedidos por Sede</Title>
          <Chart
            options={optionsPieSedes}
            series={seriesPieSedes}
            type="donut"
            height={350}
          />
        </Card>
      </div>

      {/* Tabla de Ranking */}
      <Card>
        <Title className="mb-4 flex items-center gap-2">
          <Award className="text-yellow-500 w-5 h-5" />
          Ranking Detallado de Sedes
        </Title>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left p-3 font-bold text-slate-700">#</th>
                <th className="text-left p-3 font-bold text-slate-700">Sede</th>
                <th className="text-left p-3 font-bold text-slate-700">Dirección</th>
                <th className="text-center p-3 font-bold text-slate-700">Total Pedidos</th>
                <th className="text-center p-3 font-bold text-slate-700">% del Total</th>
                <th className="text-left p-3 font-bold text-slate-700">Tipos más pedidos</th>
              </tr>
            </thead>
            <tbody>
              {rankingSedes.map((sede, index) => {
                // Top 3 tipos más pedidos de esta sede
                const topTipos = Object.entries(sede.porTipo)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3);

                return (
                  <tr key={sede.nombre} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="p-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                        index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-600' :
                        index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800' :
                        'bg-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="text-blue-500 w-4 h-4" />
                        <span className="font-semibold">{sede.nombre}</span>
                      </div>
                    </td>
                    <td className="p-3 text-slate-600 text-xs">{sede.direccion}</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-bold">
                        {sede.total}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="font-semibold text-slate-700">{sede.porcentaje}%</span>
                    </td>
                    <td className="p-3">
                      <div className="space-y-1">
                        {topTipos.map(([tipo, cantidad]) => (
                          <div key={tipo} className="flex items-center gap-2 text-xs">
                            <span className="bg-slate-100 px-2 py-0.5 rounded font-medium text-slate-700">
                              {tipo}
                            </span>
                            <span className="text-slate-500">({cantidad})</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Análisis por Tipo de Menú */}
      <Card>
        <Title className="mb-4">Preferencias por Sede</Title>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rankingSedes.map((sede) => {
            const topTipo = Object.entries(sede.porTipo)
              .sort((a, b) => b[1] - a[1])[0];
            
            return (
              <div key={sede.nombre} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Text className="font-bold text-slate-900">{sede.nombre}</Text>
                    <Text className="text-xs text-slate-500">{sede.total} pedidos</Text>
                  </div>
                  <MapPin className="text-blue-500 w-4 h-4" />
                </div>
                <div className="space-y-2">
                  {Object.entries(sede.porTipo)
                    .sort((a, b) => b[1] - a[1])
                    .map(([tipo, cantidad]) => {
                      const porcentaje = ((cantidad / sede.total) * 100).toFixed(0);
                      return (
                        <div key={tipo}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-700 font-medium">{tipo}</span>
                            <span className="text-slate-500">{cantidad} ({porcentaje}%)</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${porcentaje}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default RankingSedes;
